/* -------------------------------------------------------------------------- */
/*  TheProblem.tsx                                                            */
/* -------------------------------------------------------------------------- */

import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { motion } from "framer-motion";

import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import FollowTheSignsOutlinedIcon from "@mui/icons-material/FollowTheSignsOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";

const topProblem = {
  text: "We run lots of outreach, but it doesn't feel connected.",
  icon: EventBusyOutlinedIcon,
};

const bottomProblem = {
  text: "How do we know whether our evangelism is actually improving?",
  icon: TrendingUpOutlinedIcon,
};

const leftProblems = [
  {
    text: "How do we know what to prioritise next?",
    icon: QuestionMarkOutlinedIcon,
  },
  {
    text: "My team has valuable conversations, but I rarely hear the details.",
    icon: ForumOutlinedIcon,
  },
  {
    text: "Important information is buried in WhatsApp chats and spreadsheets.",
    icon: FolderOffOutlinedIcon,
  },
];

const rightProblems = [
  {
    text: "We don't know which approaches are producing meaningful engagement.",
    icon: InsightsOutlinedIcon,
  },
  {
    text: "People express interest, but are we consistently following them up?",
    icon: FollowTheSignsOutlinedIcon,
  },
  {
    text: "Are we reaching the people and places we intended to?",
    icon: PersonSearchOutlinedIcon,
  },
];

interface ProblemCardProps {
  text: string;
  icon: React.ElementType;
  delay?: number;
}

const ProblemCard: React.FC<ProblemCardProps> = ({
  text,
  icon: Icon,
  delay = 0,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.5,
        delay,
      }}
      style={{ width: "100%" }}
    >
      <Card
        sx={{
          background: "#1e293b",
          borderRadius: 3,
          boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
          height: "100%",
          border: "1px solid rgba(255,255,255,0.04)",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",

          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
          },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            p: 3,

            "&:last-child": {
              pb: 3,
            },
          }}
        >
          <Icon
            sx={{
              color: "#4ade80",
              fontSize: 28,
              flexShrink: 0,
              mt: 0.1,
            }}
          />

          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontWeight: 600,
              lineHeight: 1.6,
            }}
          >
            {text}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const TheProblem = () => {
  return (
    <Box
      component="section"
      id="problem"
      sx={{
        width: "100%",
        py: { xs: 10, md: 14 },
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              textAlign: "center",
              mb: { xs: 6, md: 8 },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "white",
                fontWeight: 900,
                letterSpacing: -0.7,
                mb: 2,
              }}
            >
              Overwhelmed by the Complexity of Leading Evangelism Teams?
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.82)",
                maxWidth: 760,
                mx: "auto",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              As churches grow, evangelism ministries become too complex for any
              single person to hold the whole picture in their head.
            </Typography>
          </Box>
        </motion.div>

        {/* Top problem */}
        <Box
          sx={{
            maxWidth: 620,
            mx: "auto",
            mb: { xs: 3, md: 4 },
          }}
        >
          <ProblemCard
            text={topProblem.text}
            icon={topProblem.icon}
            delay={0.05}
          />
        </Box>

        {/* Middle layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr minmax(300px, 0.95fr) 1fr",
            },
            gap: {
              xs: 3,
              md: 4,
            },
            alignItems: "center",
          }}
        >
          {/* Left problems */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {leftProblems.map((problem, index) => (
              <ProblemCard
                key={problem.text}
                text={problem.text}
                icon={problem.icon}
                delay={0.1 + index * 0.06}
              />
            ))}
          </Box>

          {/* Central button */}
          <Box
            component={motion.a}
            href="/solutions/"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{
              duration: 0.55,
              delay: 0.16,
            }}
            sx={{
              textDecoration: "none",
              display: "block",
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                px: {
                  xs: 3,
                  sm: 4,
                  md: 4,
                },
                py: {
                  xs: 4,
                  md: 5,
                },
                borderRadius: 4,
                background: "rgba(15,23,42,0.94)",
                border: "1px solid rgba(255,255,255,0.14)",
                boxShadow: "0 18px 50px rgba(0,0,0,0.32)",
                textAlign: "center",
                transition:
                  "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",

                "&:hover": {
                  transform: "translateY(-4px)",
                  borderColor: "rgba(255,255,255,0.28)",
                  boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
                },
              }}
            >
              <Typography
                sx={{
                  color: "white",
                  fontWeight: 850,
                  fontSize: {
                    xs: "1.45rem",
                    sm: "1.65rem",
                    md: "1.8rem",
                  },
                  lineHeight: 1.2,
                  letterSpacing: "-0.025em",
                }}
              >
                We&apos;re doing lots of evangelism. 
                But we are missing an overall strategy.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  mt: 2.5,
                  color: "#4ade80",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 800,
                  }}
                >
                  See how PATHWAYS can help
                </Typography>

                <ArrowForward sx={{ fontSize: 18 }} />
              </Box>
            </Box>
          </Box>

          {/* Right problems */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {rightProblems.map((problem, index) => (
              <ProblemCard
                key={problem.text}
                text={problem.text}
                icon={problem.icon}
                delay={0.1 + index * 0.06}
              />
            ))}
          </Box>
        </Box>

        {/* Bottom problem */}
        <Box
          sx={{
            maxWidth: 620,
            mx: "auto",
            mt: { xs: 3, md: 4 },
          }}
        >
          <ProblemCard
            text={bottomProblem.text}
            icon={bottomProblem.icon}
            delay={0.3}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default TheProblem;